// Cinematic primitives — film grain, ambient drifting gradients, scanlines,
// HUD details, scroll cue, watermark, count-up, reveal wrappers.
import { useEffect, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import monoTransparent from "../../assets/brand/logo_monogram_transparent.png";

/** Cinematic base layer: film grain + 2 drifting ambient golds + faint scanlines. */
export function CinematicCanvas({ scanlines = false, className = "" }) {
  return (
    <div aria-hidden className={`pointer-events-none fixed inset-0 -z-10 overflow-hidden ${className}`}>
      {/* Drifting ambient gold #1 (top-right) */}
      <div className="absolute -top-[20%] -right-[10%] h-[120vh] w-[120vh] rounded-full opacity-[0.12] mix-blend-screen ambient-1"
           style={{ background: "radial-gradient(closest-side, rgba(229,197,106,0.85), rgba(201,168,76,0.2) 35%, transparent 70%)", filter: "blur(120px)" }} />
      {/* Drifting ambient gold #2 (bottom-left) */}
      <div className="absolute -bottom-[25%] -left-[15%] h-[110vh] w-[110vh] rounded-full opacity-[0.08] mix-blend-screen ambient-2"
           style={{ background: "radial-gradient(closest-side, rgba(169,140,54,0.75), rgba(139,0,0,0.12) 45%, transparent 75%)", filter: "blur(140px)" }} />
      {/* Film grain — inline SVG noise, ultra-subtle so #0A0A0A stays deep */}
      <svg className="absolute inset-0 h-full w-full opacity-[0.025] mix-blend-overlay" xmlns="http://www.w3.org/2000/svg">
        <filter id="omb-noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.96   0 0 0 0 0.94   0 0 0 0 0.91   0 0 0 0.55 0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#omb-noise)" />
      </svg>
      {/* Faint vertical scanlines for HUD feel — kept ultra-subtle */}
      {scanlines && (
        <div className="absolute inset-0 opacity-[0.012]"
             style={{ background: "repeating-linear-gradient(90deg, transparent 0, transparent 3px, rgba(245,240,232,1) 3px, rgba(245,240,232,1) 4px)" }} />
      )}
      {/* Vignette */}
      <div className="absolute inset-0"
           style={{ background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.6) 100%)" }} />
    </div>
  );
}

/** Large faded monogram watermark — sits behind hero, opacity 0.03, rotated. */
export function MonogramWatermark({ className = "" }) {
  return (
    <div aria-hidden className={`pointer-events-none absolute z-0 ${className}`}>
      <img src={monoTransparent} alt=""
           className="select-none opacity-[0.035]"
           style={{ width: "min(1100px, 95vw)", filter: "saturate(0) brightness(2)", transform: "rotate(-8deg)" }} />
    </div>
  );
}

/** Pulsing live-status dot (HUD). */
export function HudDot({ crimson = false, className = "" }) {
  return <span className={`hud-dot ${crimson ? "live-crimson" : ""} ${className}`} />;
}

/** Top-left + bottom-right hairline corner brackets — Iron Man targeting feel. */
export function CornerBrackets({ size = 18, opacity = 0.45 }) {
  const c = `rgba(201,168,76,${opacity})`;
  const s = `${size}px`;
  return (
    <>
      <span aria-hidden className="pointer-events-none absolute left-2 top-2"
            style={{ width: s, height: s, borderTop: `1px solid ${c}`, borderLeft: `1px solid ${c}` }} />
      <span aria-hidden className="pointer-events-none absolute right-2 bottom-2"
            style={{ width: s, height: s, borderBottom: `1px solid ${c}`, borderRight: `1px solid ${c}` }} />
    </>
  );
}

/** Editorial eyebrow: " — — 01 / SERVICES " */
export function Eyebrow({ index, children, className = "" }) {
  return (
    <span className={`eyebrow font-mono ${className}`}>
      {index && <span className="text-brand-gold/60">{index} /</span>} {children}
    </span>
  );
}

/** Scroll cue — gold vertical line + tiny SCROLL caps that bobs. */
export function ScrollCue({ className = "" }) {
  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      <span className="block h-12 w-px bg-gradient-to-b from-brand-gold/0 via-brand-gold/60 to-brand-gold/0" />
      <span className="text-[10px] uppercase tracking-[0.4em] text-brand-text/40" style={{ animation: "scroll-bob 2.4s ease-in-out infinite" }}>Scroll</span>
    </div>
  );
}

/** IntersectionObserver-based count-up. `to` may be a string like "$8.4M" (numeric part counted). */
export function CountUp({ to, durationMs = 1400, className = "" }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });

  // Extract numeric value + prefix/suffix
  const match = String(to).match(/^([^\d]*)([\d.,]+)([^\d]*)$/);
  const prefix = match?.[1] || "";
  const numericStr = match?.[2] || "0";
  const suffix = match?.[3] || "";
  const target = parseFloat(numericStr.replace(/,/g, "")) || 0;
  const hasDecimal = numericStr.includes(".");

  useEffect(() => {
    if (!inView) return;
    let raf, start;
    const tick = (t) => {
      if (start == null) start = t;
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, durationMs]);

  const display = hasDecimal ? val.toFixed(1) : Math.round(val).toLocaleString();
  return <span ref={ref} className={className}>{prefix}{display}{suffix}</span>;
}

/** Reveal wrapper — fade-up on view, optional stagger via delay prop. */
export function Reveal({ children, delay = 0, y = 24, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.8, ease: [0.2, 0.7, 0.2, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/** Hairline rule that draws in from left on view. */
export function DrawRule({ width = "w-12", className = "" }) {
  return (
    <motion.div
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true, amount: 0.6 }}
      transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
      style={{ transformOrigin: "left" }}
      className={`h-px ${width} bg-gradient-to-r from-brand-gold via-brand-gold-light to-brand-gold/0 ${className}`}
    />
  );
}

/** Parallax wrapper for the hero — translates y based on scroll. */
export function HeroParallax({ children, range = [0, -120], className = "" }) {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], range);
  return <motion.div ref={ref} style={{ y }} className={className}>{children}</motion.div>;
}

/** HUD status bar — date/time + live indicator on top-right. */
export function HudStatusBar() {
  const [time, setTime] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  const hhmm = time.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "America/New_York" });
  return (
    <div className="hidden items-center gap-4 text-[10px] uppercase tracking-[0.32em] text-brand-text/45 sm:flex font-mono">
      <span>EST · ATL</span>
      <span className="h-px w-6 bg-brand-text/15" />
      <span>{hhmm}</span>
      <HudDot />
    </div>
  );
}
